import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { supabaseDB, type UserProfile } from '@/lib/supabase';

export interface DashboardData {
  userProfile: UserProfile;
  reviews: CodeReviewSummary[];
  stats: DashboardStats;
  repoMetrics: RepositoryMetrics[];
  openPullRequests: PullRequest[];
  recentMerges: PullRequest[];
}

export interface DailyPrStat {
  date: string;
  opened_prs: number;
  reviews_count: number;
  issues_found: number;
  merged_prs?: number;
}

export interface WeeklyPulseData {
  prsOpened: number;
  prsMerged: number;
  avgMergeTimeHours: number;
  linesChanged: number;
}

// Define your types based on your database schema
export interface Repository {
  id: string;
  name: string;
  fullName: string;
  description?: string;
  url: string;
  lastReviewedAt: string | null;
  issuesCount: number;
  is_active?: boolean;
  review_enabled?: boolean;
  github_installation_id?: string;
  include_summary?: boolean;
  auto_review_enabled?: boolean;
  scan_frequency?: 'per_pr' | 'per_commit';
}

export interface CodeReviewSummary {
  id: string;
  repositoryName: string;
  pullRequestNumber: number;
  title: string;
  status: 'completed' | 'in_progress' | 'pending';
  createdAt: string;
  issuesFound: number;
}

export interface DashboardStats {
  totalReviews: number;
  remainingReviews: number;
  issuesFound: number;
  activeRepositories: number;
  totalCost: number;
}

export interface InstallationStatus {
  is_installed: boolean;
  installation_url: string;
}

export interface CustomRule {
  id: string;
  user_id: string;
  title: string;
  description?: string;
  rule_content: string;
  is_public: boolean;
  usage_count: number;
  created_at: string;
  updated_at: string;
}


export interface RepositoryRule {
  id: string;
  custom_rule_id: string;
  title: string;
  description?: string;
  rule_content: string;
  is_enabled: boolean;
  order_index: number;
  is_public: boolean;
}

export interface CreateRuleRequest {
  title: string;
  description?: string;
  rule_content: string;
  is_public?: boolean;
}

export interface GetRulesRequest {
  is_public?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface GetRulesResponse {
  rules: CustomRule[];
  total: number;
  has_more: boolean;
}

export interface PullRequest {
  id: string;
  pr_number: number;
  pr_id: string;
  repository_id: string;
  repo_url: string;
  user_id: string;
  title: string;
  description?: string;
  author_username?: string;
  author_avatar_url?: string;
  state: 'open' | 'closed' | 'merged';
  is_draft: boolean;
  lines_added: number;
  lines_deleted: number;
  lines_changed: number;
  files_changed: number;
  commits_count: number;
  created_at: string;
  updated_at: string;
  closed_at?: string;
  merged_at?: string;
  merge_time_hours?: number;
  review_status: 'pending' | 'in_progress' | 'completed' | 'failed';
  total_scans: number;
  last_reviewed_at?: string;
  issues_found: number;
  critical_issues: number;
  warnings: number;
  suggestions: number;
  trivial_issues: number;
  html_url: string;
  metadata?: Record<string, unknown>;
  synced_at: string;
}

export interface PullRequestDetails extends PullRequest {
  diff: string | null;
  timeline: TimelineEvent[];
  review: CodeReview | null;
}

export type TimelineEvent = {
  id: string;
  type: 'comment' | 'review' | 'commit' | 'merge' | 'close' | 'reopen';
  user: {
    name: string;
    avatar_url: string;
  };
  body: string | null;
  created_at: string;
};

export interface CodeReview {
  id: string;
  summary: string;
  issues: CodeIssue[];
}

export interface CodeIssue {
  id: string;
  file_path: string;
  line_start: number;
  line_end: number;
  comment: string;
  severity: 'critical' | 'warning' | 'suggestion' | 'trivial';
}

export interface RepositoryMetrics {
  repoName: string;
  repoUrl: string;
  repositoryId: string;
  totalPRs: number;
  totalPRsMerged: number;
  openPRs: number;
  prsThisWeek: number;
  prsThisMonth: number;
  avgMergeTimeHours: number;
  avgPRLinesAdded: number;
  avgPRLinesDeleted: number;
  avgPRLinesChanged: number;
  totalIssuesFound: number;
  avgIssuesPerPR: number;
  totalScans: number;
  scanStatus: 'active' | 'inactive' | 'error';
}

interface MetricRow {
  reponame: string;
  repourl: string;
  repositoryid: string;
  totalprs: string | number;
  totalprsmerged: string | number;
  openprs: string | number;
  prsthisweek: string | number;
  prsthismonth: string | number;
  avgmergetimehours: string | number;
  avgprlinesadded: string | number;
  avgprlinesdeleted: string | number;
  avgprlineschanged: string | number;
  totalissuesfound: string | number;
  avgissuesperpr: string | number;
  totalscans: string | number;
  is_active?: boolean;
  review_enabled?: boolean;
}

interface ScanResults {
  issues?: unknown[];
  title?: string;
  summary?: string;
}

class SupabaseService {
  private client: SupabaseClient;

  constructor() {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

    if (!supabaseUrl) {
      console.error('Supabase URL must be configured in environment variables');
    }
    if (!supabaseKey) {
      console.error('Supabase anon key must be configured in environment variables');
    }

    this.client = createClient(supabaseUrl, supabaseKey);
  }

  // Authentication methods
  async getCurrentUser() {
    const { data } = await this.client.auth.getUser();
    return data.user;
  }

  async getUserProfile(userId: string) {
    try {
      const user = await supabaseDB.getUserProfile(userId);
      return user;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  }

  async getSession() {
    const { data } = await this.client.auth.getSession();
    return data.session;
  }

  // GitHub installation status
  async getInstallationStatus(): Promise<InstallationStatus> {
    const { data: user } = await this.client.auth.getUser();

    if (!user.user) {
      throw new Error('User not authenticated');
    }

    // Check if the user has a GitHub installation ID
    const { data, error } = await this.client
      .from('users')
      .select('github_installation_id')
      .eq('id', user.user.id)
      .single();

    if (error) {
      console.error('Error fetching installation status:', error);
      throw error;
    }

    // Generate the callback URL with your domain
    const callbackUrl = encodeURIComponent(`${window.location.origin}/github-callback`);
    return {
      is_installed: data?.github_installation_id !== null,
      installation_url: `https://github.com/apps/Codii-Dev/installations/new?redirect_uri=${callbackUrl}`
    };
  }

  // Repository methods
  async getRepositories(): Promise<Repository[]> {
    const { data: userSession } = await this.client.auth.getUser();

    if (!userSession.user) {
      throw new Error('User not authenticated');
    }

    // Fetch repositories directly from the new 'repositories' table
    const { data, error } = await this.client
      .from('repositories')
      .select('id, name, repo_url, description, is_active, review_enabled, github_installation_id, created_at')
      .eq('user_id', userSession.user.id)
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching repositories:', error);
      throw error;
    }

    // Convert to the expected Repository format
    return data.map(repo => ({
      id: repo.id,
      name: repo.name,
      fullName: repo.name,
      description: repo.description,
      url: repo.repo_url,
      is_active: repo.is_active,
      review_enabled: repo.review_enabled,
      github_installation_id: repo.github_installation_id,
      lastReviewedAt: null,
      issuesCount: 0,
    }));
  }

  async getRepositoryByUrl(url: string): Promise<Repository | null> {
    const { data: userSession } = await this.client.auth.getUser();
    if (!userSession.user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await this.client
      .from('repositories')
      .select('*')
      .eq('repo_url', url)
      .eq('user_id', userSession.user.id)
      .single();

    if (error) {
      console.error('Error fetching repository by URL:', error);
      throw error;
    }

    return data;
  }



  async getRepositoryById(id: string): Promise<Repository | null> {
    const { data: userSession } = await this.client.auth.getUser();
    if (!userSession.user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await this.client
      .from('repositories') // Query the new repositories table
      .select('id, name, repo_url, description, is_active, review_enabled, github_installation_id, auto_review_enabled, include_summary')
      .eq('id', id)
      .eq('user_id', userSession.user.id) // Ensure user owns this repo config
      .single();

    if (error) {
      console.error('Error fetching repository by ID:', error);
      // Distinguish between not found and other errors if needed
      if (error.code === 'PGRST116') { // PGRST116: "The result contains 0 rows"
        return null;
      }
      throw error;
    }

    if (!data) return null;

    // Adapt to the Repository interface
    return {
      id: data.id, // This is now a UUID string
      name: data.name, // This is owner/repo
      fullName: data.name, // Assuming name is owner/repo, fullName is the same
      description: data.description,
      url: data.repo_url,
      is_active: data.is_active,
      review_enabled: data.review_enabled,
      github_installation_id: data.github_installation_id,
      auto_review_enabled: data.auto_review_enabled,
      include_summary: data.include_summary,
      // These fields are not directly on the repositories table, set to defaults or fetch if critical for this view
      lastReviewedAt: null,
      issuesCount: 0,
    };
  }


  async updateRepositoryReviewEnabled(id: string, enabled: boolean): Promise<void> {
    const { data: userSession } = await this.client.auth.getUser();
    if (!userSession.user) {
      throw new Error('User not authenticated');
    }
    const { error } = await this.client
      .from('repositories')
      .update({ review_enabled: enabled, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('user_id', userSession.user.id);

    if (error) {
      console.error('Error updating repository review enabled status:', error);
      throw error;
    }
  }

  async updateRepositoryAutoReviewEnabled(id: string, enabled: boolean): Promise<void> {
    const { data: userSession } = await this.client.auth.getUser();
    if (!userSession.user) {
      throw new Error('User not authenticated');
    }
    const { error } = await this.client
      .from('repositories')
      .update({ auto_review_enabled: enabled, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('user_id', userSession.user.id);

    if (error) {
      console.error('Error updating repository auto review enabled status:', error);
      throw error;
    }
  }

  async updateRepositoryIncludeSummary(id: string, includeSummary: boolean): Promise<void> {
    const { data: userSession } = await this.client.auth.getUser();
    if (!userSession.user) {
      throw new Error('User not authenticated');
    }
    const { error } = await this.client
      .from('repositories')
      .update({ include_summary: includeSummary, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('user_id', userSession.user.id);

    if (error) {
      console.error('Error updating repository `include summary`:', error);
      throw error;
    }
  }

  async updateRepositoryScanFrequency(id: string, scanFrequency: 'per_pr' | 'per_commit'): Promise<void> {
    const { data: userSession } = await this.client.auth.getUser();
    if (!userSession.user) {
      throw new Error('User not authenticated');
    }
    const { error } = await this.client
      .from('repositories')
      .update({ scan_frequency: scanFrequency, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('user_id', userSession.user.id);

    if (error) {
      console.error('Error updating repository scan frequency:', error);
      throw error;
    }
  }

  async updateRepositorySettings(
    id: string,
    settings: {
      reviewEnabled?: boolean;
      includeSummary?: boolean;
      autoReviewEnabled?: boolean;
      scanFrequency?: 'per_pr' | 'per_commit';
    }
  ): Promise<void> {
    const { data: userSession } = await this.client.auth.getUser();
    if (!userSession.user) {
      throw new Error('User not authenticated');
    }

    // Build update object with only provided settings
    const updateData: Record<string, unknown> = { updated_at: new Date().toISOString() };

    if (settings.reviewEnabled !== undefined) {
      updateData.review_enabled = settings.reviewEnabled;
    }
    if (settings.includeSummary !== undefined) {
      updateData.include_summary = settings.includeSummary;
    }
    if (settings.autoReviewEnabled !== undefined) {
      updateData.auto_review_enabled = settings.autoReviewEnabled;
    }
    if (settings.scanFrequency !== undefined) {
      updateData.scan_frequency = settings.scanFrequency;
    }

    const { error } = await this.client
      .from('repositories')
      .update(updateData)
      .eq('id', id)
      .eq('user_id', userSession.user.id);

    if (error) {
      console.error('Error updating repository settings:', error);
      throw error;
    }
  }

  // Code Review methods
  async getCodeReviews(repositoryId?: string): Promise<CodeReviewSummary[]> {
    const { data: user } = await this.client.auth.getUser();

    if (!user.user) {
      throw new Error('User not authenticated');
    }

    let query = this.client
      .from('repo_scans')
      .select('*')
      .eq('user_id', user.user.id);

    if (repositoryId) {
      const repo = await this.getRepositoryById(repositoryId);
      if (repo) {
        query = query.eq('repo_url', repo.url);
      }
    }

    const { data, error } = await query
      .order('scan_date', { ascending: false })
      .limit(50); // Fetch more to allow for client-side deduplication

    if (error) {
      console.error('Error fetching code reviews:', error);
      throw error;
    }

    // Deduplicate scans by PR (keep latest)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const uniqueScans = new Map<string, any>();
    for (const scan of data) {
      // Create a unique key for each PR: repo_url + pull_request_id
      // If pull_request_id is missing, fallback to scan id
      const key = `${scan.repo_url}-${scan.pull_request_id || scan.id}`;

      if (!uniqueScans.has(key)) {
        uniqueScans.set(key, scan);
      }

      if (uniqueScans.size >= 10) break;
    }

    // Convert to the expected CodeReviewSummary format
    return Array.from(uniqueScans.values()).map(scan => {
      // Extract repository name from repo_url
      const urlParts = scan.repo_url.split('/');
      const repoName = urlParts.slice(-2).join('/').replace('.git', '');

      // Extract or generate pull request number
      let prNumber = 0;
      if (scan.pull_request_id) {
        const prMatch = scan.pull_request_id.match(/\d+/);
        if (prMatch) {
          prNumber = parseInt(prMatch[0], 10);
        }
      }

      // Determine status based on scan_status
      let status: 'completed' | 'in_progress' | 'pending' = 'pending';
      switch (scan.scan_status) {
        case 'completed':
          status = 'completed';
          break;
        case 'in_progress':
          status = 'in_progress';
          break;
        default:
          status = 'pending';
      }

      // Count issues
      let issuesFound = 0;
      let title = 'Code Review';

      const results = scan.results as ScanResults | null;

      if (results) {
        if (results.issues && Array.isArray(results.issues)) {
          issuesFound = results.issues.length;
        }
        if (results.title) {
          title = results.title;
        }
      }

      return {
        id: scan.id,
        repositoryName: repoName,
        pullRequestNumber: prNumber,
        title,
        status,
        createdAt: scan.scan_date,
        issuesFound
      };
    });
  }

  // Dashboard statistics
  async getDashboardStats(): Promise<DashboardStats> {
    const { data: user } = await this.client.auth.getUser();

    if (!user.user) {
      throw new Error('User not authenticated');
    }

    const [
      remainingReviewsResult,
      repoScansResult,
      activeReposResult
    ] = await Promise.all([
      this.client
        .from('users')
        .select('remaining_scans')
        .eq('id', user.user.id)
        .single(),
      this.client
        .from('repo_scans')
        .select('cost, results')
        .eq('user_id', user.user.id),
      this.client
        .from('repositories')
        .select('repo_url')
        .eq('user_id', user.user.id)
        .eq('is_active', true)
        .order('repo_url')
    ]);

    const { data: remainingReviewsResponse, error: remainingReviewsError } = remainingReviewsResult;
    if (remainingReviewsError) {
      console.error('Error fetching remaining reviews:', remainingReviewsError);
      throw remainingReviewsError;
    }

    const { data: repoScans, error: repoScansError } = repoScansResult;
    if (repoScansError) {
      console.error('Error fetching repo scans:', repoScansError);
      throw repoScansError;
    }

    const { data: activeReposData, error: activeReposError } = activeReposResult;
    if (activeReposError) {
      console.error('Error fetching active repos:', activeReposError);
      throw activeReposError;
    }

    const totalReviews = repoScans?.length ?? 0;
    let totalCost = 0;
    let issuesFound = 0;

    if (repoScans) {
      for (const scan of repoScans) {
        totalCost += scan.cost ?? 0;
        const results = scan.results as ScanResults | null;
        if (results && Array.isArray(results.issues)) {
          issuesFound += results.issues.length;
        }
      }
    }

    const uniqueRepos = new Set(activeReposData.map(scan => scan.repo_url));
    const activeRepositories = uniqueRepos.size;

    return {
      totalReviews: totalReviews || 0,
      issuesFound,
      activeRepositories,
      remainingReviews: remainingReviewsResponse.remaining_scans || 0,
      totalCost: parseFloat(totalCost.toFixed(2)) || 0
    };
  }

  // Custom Rules methods
  async getRepositoryRules(repositoryId: string): Promise<RepositoryRule[]> {
    const { data: userSession } = await this.client.auth.getUser();
    if (!userSession.user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await this.client
      .from('repository_rules')
      .select(`
        id,
        custom_rule_id,
        is_enabled,
        order_index,
        created_at,
        custom_rules!inner(
          id,
          title,
          description,
          rule_content,
          is_public
        )
      `)
      .eq('repository_id', repositoryId)
      .order('order_index', { ascending: true });

    if (error) {
      console.error('Error fetching repository rules:', error);
      throw error;
    }

    return data.map(item => {
      // We assume custom_rules exists due to inner join
      const rule = item.custom_rules as unknown as CustomRule;
      return {
        id: item.id,
        custom_rule_id: item.custom_rule_id,
        title: rule.title,
        description: rule.description,
        rule_content: rule.rule_content,
        is_enabled: item.is_enabled,
        order_index: item.order_index,
        is_public: rule.is_public
      };
    });
  }

  async getRuleContextForRepository(repositoryId: string): Promise<string> {
    const rules = await this.getRepositoryRules(repositoryId);
    const enabledRules = rules.filter(rule => rule.is_enabled);

    if (enabledRules.length === 0) {
      return '';
    }

    return enabledRules
      .sort((a, b) => a.order_index - b.order_index)
      .map(rule => `**${rule.title}**:\n${rule.rule_content}`)
      .join('\n\n');
  }

  async getRepositoriesForRule(ruleId: string): Promise<string[]> {
    const { data, error } = await this.client
      .from('repository_rules')
      .select('repository_id')
      .eq('custom_rule_id', ruleId);

    if (error) {
      console.error('Error fetching repositories for rule:', error);
      throw error;
    }

    return data.map(item => item.repository_id);
  }

  async getDashboardData(): Promise<DashboardData> {
    const { data: userSession } = await this.client.auth.getUser();
    if (!userSession.user) {
      throw new Error('User not authenticated');
    }

    const userId = userSession.user.id;

    const [
      userProfile,
      reviews,
      stats,
      repoMetrics,
      openPullRequests,
      recentMerges
    ] = await Promise.all([
      this.getUserProfile(userId),
      this.getCodeReviews(),
      this.getDashboardStats(),
      this.getRepositoryMetrics(),
      this.getOpenPullRequests(),
      this.getRecentMerges()
    ]);

    return {
      userProfile,
      reviews,
      stats,
      repoMetrics,
      openPullRequests,
      recentMerges
    };
  }

  async getDailyPrStats(repositoryId?: string): Promise<DailyPrStat[]> {
    const { data: userSession } = await this.client.auth.getUser();
    if (!userSession.user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await this.client.rpc('get_daily_pr_stats', {
      p_user_id: userSession.user.id,
      p_repository_id: repositoryId,
    });

    if (error) {
      console.error('Error fetching daily PR stats:', error);
      throw error;
    }

    return data;
  }

  // Custom Rules CRUD methods
  async getCustomRules(filters: GetRulesRequest = {}): Promise<GetRulesResponse> {
    const { data: userSession } = await this.client.auth.getUser();
    if (!userSession.user) {
      throw new Error('User not authenticated');
    }

    let query = this.client
      .from('custom_rules')
      .select('*', { count: 'exact' })
      .eq('user_id', userSession.user.id);

    // Apply filters
    if (filters.is_public !== undefined) {
      query = query.eq('is_public', filters.is_public);
    }
    if (filters.search) {
      query = query.or(`title.ilike.%${filters.search}%,description.ilike.%${filters.search}%,rule_content.ilike.%${filters.search}%`);
    }

    // Apply pagination
    const limit = filters.limit || 50;
    const offset = filters.offset || 0;
    query = query.range(offset, offset + limit - 1);

    const { data, error, count } = await query.order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching custom rules:', error);
      throw error;
    }

    return {
      rules: data || [],
      total: count || 0,
      has_more: (count || 0) > offset + limit
    };
  }

  async getPublicRules(filters: GetRulesRequest = {}): Promise<GetRulesResponse> {
    let query = this.client
      .from('custom_rules')
      .select('*', { count: 'exact' })
      .eq('is_public', true);

    // Apply filters
    if (filters.search) {
      query = query.or(`title.ilike.%${filters.search}%,description.ilike.%${filters.search}%,rule_content.ilike.%${filters.search}%`);
    }

    // Apply pagination
    const limit = filters.limit || 50;
    const offset = filters.offset || 0;
    query = query.range(offset, offset + limit - 1);

    const { data, error, count } = await query.order('usage_count', { ascending: false });

    if (error) {
      console.error('Error fetching public rules:', error);
      throw error;
    }

    return {
      rules: data || [],
      total: count || 0,
      has_more: (count || 0) > offset + limit
    };
  }

  async createCustomRule(ruleData: CreateRuleRequest): Promise<CustomRule> {
    const { data: userSession } = await this.client.auth.getUser();
    if (!userSession.user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await this.client
      .from('custom_rules')
      .insert({
        user_id: userSession.user.id,
        title: ruleData.title,
        description: ruleData.description,
        rule_content: ruleData.rule_content,
        is_public: ruleData.is_public || false
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating custom rule:', error);
      throw error;
    }

    return data;
  }

  async updateCustomRule(ruleId: string, updates: Partial<CreateRuleRequest>): Promise<CustomRule> {
    const { data: userSession } = await this.client.auth.getUser();
    if (!userSession.user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await this.client
      .from('custom_rules')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', ruleId)
      .eq('user_id', userSession.user.id)
      .select()
      .single();

    if (error) {
      console.error('Error updating custom rule:', error);
      throw error;
    }

    return data;
  }

  async deleteCustomRule(ruleId: string): Promise<void> {
    const { data: userSession } = await this.client.auth.getUser();
    if (!userSession.user) {
      throw new Error('User not authenticated');
    }

    const { error } = await this.client
      .from('custom_rules')
      .delete()
      .eq('id', ruleId)
      .eq('user_id', userSession.user.id);

    if (error) {
      console.error('Error deleting custom rule:', error);
      throw error;
    }
  }

  // Repository Rules management methods
  async addRuleToRepository(repositoryId: string, ruleId: string): Promise<void> {
    const { data: userSession } = await this.client.auth.getUser();
    if (!userSession.user) {
      throw new Error('User not authenticated');
    }

    // Get the next order index
    const { data: existingRules } = await this.client
      .from('repository_rules')
      .select('order_index')
      .eq('repository_id', repositoryId)
      .order('order_index', { ascending: false })
      .limit(1);

    const nextOrderIndex = existingRules && existingRules.length > 0
      ? existingRules[0].order_index + 1
      : 0;

    const { error } = await this.client
      .from('repository_rules')
      .insert({
        repository_id: repositoryId,
        custom_rule_id: ruleId,
        order_index: nextOrderIndex
      });

    if (error) {
      console.error('Error adding rule to repository:', error);
      throw error;
    }
  }

  async removeRuleFromRepository(repositoryId: string, ruleId: string): Promise<void> {
    const { data: userSession } = await this.client.auth.getUser();
    if (!userSession.user) {
      throw new Error('User not authenticated');
    }

    const { error } = await this.client
      .from('repository_rules')
      .delete()
      .eq('repository_id', repositoryId)
      .eq('custom_rule_id', ruleId);

    if (error) {
      console.error('Error removing rule from repository:', error);
      throw error;
    }
  }

  async toggleRepositoryRule(repositoryId: string, ruleId: string, enabled: boolean): Promise<void> {
    const { data: userSession } = await this.client.auth.getUser();
    if (!userSession.user) {
      throw new Error('User not authenticated');
    }

    const { error } = await this.client
      .from('repository_rules')
      .update({ is_enabled: enabled })
      .eq('repository_id', repositoryId)
      .eq('custom_rule_id', ruleId);

    if (error) {
      console.error('Error toggling repository rule:', error);
      throw error;
    }
  }

  async reorderRepositoryRules(repositoryId: string, ruleOrders: { ruleId: string; orderIndex: number }[]): Promise<void> {
    const { data: userSession } = await this.client.auth.getUser();
    if (!userSession.user) {
      throw new Error('User not authenticated');
    }

    // Update each rule's order index
    for (const { ruleId, orderIndex } of ruleOrders) {
      const { error } = await this.client
        .from('repository_rules')
        .update({ order_index: orderIndex })
        .eq('repository_id', repositoryId)
        .eq('custom_rule_id', ruleId);

      if (error) {
        console.error('Error reordering repository rules:', error);
        throw error;
      }
    }
  }

  /**
   * Get metrics for all repositories owned by the authenticated user
   * Uses the get_all_repository_metrics_for_user for optimal performance
   * @returns Array of RepositoryMetrics objects
   */
  async getRepositoryMetrics(): Promise<RepositoryMetrics[]> {
    const { data: userSession } = await this.client.auth.getUser();
    if (!userSession.user) {
      throw new Error('User not authenticated');
    }

    const { data: metrics, error } = await this.client
      .rpc('get_all_repository_metrics_for_user', { p_user_id: userSession.user.id });

    if (error) {
      console.error('Error fetching all repository metrics:', error);
      throw error;
    }

    if (!metrics) {
      return [];
    }

    return (metrics as MetricRow[]).map((metric) => {
      let scanStatus: 'active' | 'inactive' | 'error' = 'inactive';
      if (metric.is_active && metric.review_enabled) {
        scanStatus = Number(metric.prsthisweek) > 0 ? 'active' : 'inactive';
      }

      return {
        repoName: metric.reponame,
        repoUrl: metric.repourl,
        repositoryId: metric.repositoryid,
        totalPRs: Number(metric.totalprs),
        totalPRsMerged: Number(metric.totalprsmerged),
        openPRs: Number(metric.openprs),
        prsThisWeek: Number(metric.prsthisweek),
        prsThisMonth: Number(metric.prsthismonth),
        avgMergeTimeHours: Math.round(Number(metric.avgmergetimehours)),
        avgPRLinesAdded: Math.round(Number(metric.avgprlinesadded)),
        avgPRLinesDeleted: Math.round(Number(metric.avgprlinesdeleted)),
        avgPRLinesChanged: Math.round(Number(metric.avgprlineschanged)),
        totalIssuesFound: Number(metric.totalissuesfound),
        avgIssuesPerPR: Math.round(Number(metric.avgissuesperpr)),
        totalScans: Number(metric.totalscans),
        scanStatus
      };
    });
  }

  /**
   * Get recent pull requests for a repository
   * @param repositoryId - The repository ID
   * @param limit - Number of PRs to return (default 10)
   * @returns Array of PullRequest objects
   */
  async getRepositoryPullRequests(repositoryId: string, limit = 20): Promise<PullRequest[]> {
    const { data: userSession } = await this.client.auth.getUser();
    if (!userSession.user) {
      throw new Error('User not authenticated');
    }

    const { data: prs, error } = await this.client
      .from('pull_requests')
      .select('*')
      .eq('user_id', userSession.user.id)
      .eq('repository_id', repositoryId)
      .order('created_at', { ascending: false})
      .limit(limit);

    if (error) {
      console.error('Error fetching pull requests:', error);
      throw error;
    }

    return prs ?? [];
  }

  /**
   * Get a single pull request by ID
   * @param prId - The pull request ID
   * @returns PullRequest object
   */
  async getPullRequest(prId: string): Promise<PullRequest | null> {
    const { data: userSession } = await this.client.auth.getUser();
    if (!userSession.user) {
      throw new Error('User not authenticated');
    }

    const { data: pr, error } = await this.client
      .from('pull_requests')
      .select('*')
      .eq('id', prId)
      .eq('user_id', userSession.user.id)
      .single();

    if (error) {
      console.error('Error fetching pull request:', error);
      return null;
    }

    return pr;
  }

  async getPullRequestByNumber(repositoryId: string, prNumber: number): Promise<PullRequestDetails | null> {
    const { data: userSession } = await this.client.auth.getUser();
    if (!userSession.user) {
      throw new Error('User not authenticated');
    }

    const { data: pr, error } = await this.client
      .from('pull_requests')
      .select('*')
      .eq('repository_id', repositoryId)
      .eq('pr_number', prNumber)
      .eq('user_id', userSession.user.id)
      .single();

    if (error) {
      console.error('Error fetching pull request by number:', error);
      return null;
    }

    if (!pr) {
      return null;
    }

    // --- Build a realistic timeline from available data ---
    const timeline: TimelineEvent[] = [];

    // 1. PR Creation Event
    timeline.push({
      id: `${pr.id}-created`,
      type: 'commit', // Using 'commit' as a proxy for creation
      user: {
        name: pr.author_username || 'Unknown User',
        avatar_url: pr.author_avatar_url || '',
      },
      body: pr.title,
      created_at: pr.created_at,
    });

    // 2. Codii Review Events from repo_scans
    const { data: scans } = await this.client
      .from('repo_scans')
      .select('id, scan_date, results')
      .eq("repo_url", pr.repo_url)
      .eq('pull_request_id', pr.pr_number);

    if (scans) {
      for (const scan of scans) {
        const results = scan.results as ScanResults | null;
        timeline.push({
          id: scan.id,
          type: 'review',
          user: { name: 'Codii', avatar_url: '' }, // Placeholder for Codii user
          body: results?.summary || 'Review completed.',
          created_at: scan.scan_date,
        });
      }
    }

    // 3. Merged or Closed Event
    if (pr.merged_at) {
      timeline.push({
        id: `${pr.id}-merged`,
        type: 'merge',
        user: { name: pr.author_username || 'Unknown User', avatar_url: pr.author_avatar_url || '' },
        body: 'Pull request merged',
        created_at: pr.merged_at,
      });
    } else if (pr.closed_at) {
      timeline.push({
        id: `${pr.id}-closed`,
        type: 'close',
        user: { name: pr.author_username || 'Unknown User', avatar_url: pr.author_avatar_url || '' },
        body: 'Pull request closed',
        created_at: pr.closed_at,
      });
    }

    // Sort timeline by date
    timeline.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

    const review = await this.getPullRequestReview(pr.pr_number.toString());

    return { ...pr, diff: null, timeline, review };
  }

  async getPullRequestDiff(repoId: string, prNumber: number): Promise<string | null> {
    const { data: sessionData, error: sessionError } = await this.client.auth.getSession();
    if (sessionError || !sessionData.session) {
      throw new Error('User not authenticated');
    }

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.codii.dev';
      const response = await fetch(`${apiUrl}/api/repository/${repoId}/pulls/${prNumber}/diff`, {
        headers: {
          'Authorization': `Bearer ${sessionData.session.access_token}`,
        },
      });

      if (!response.ok) {
        // Handle non-2xx responses, e.g., 404 from the API
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch diff from API');
      }

      const data = await response.json();
      return data.diff;

    } catch (e) {
      console.error('Error fetching pull request diff:', e);
      // It's better to return null or let the caller handle the error display
      return null;
    }
  }

  async getReviewsForPrs(prIds: number[]): Promise<Record<number, boolean>> {
    if (!prIds || prIds.length === 0) {
      return {};
    }

    const { data, error } = await this.client
      .from('repo_scans')
      .select('pull_request_id')
      .in('pull_request_id', prIds);

    if (error) {
      console.error('Error fetching PR reviews:', error);
      return {};
    }

    const reviewedPrIds = new Set(data.map(item => item.pull_request_id));
    const reviewStatus: Record<number, boolean> = {};
    for (const id of prIds) {
      reviewStatus[id] = reviewedPrIds.has(id);
    }

    return reviewStatus;
  }

  async getPullRequestReview(prId: string): Promise<CodeReview | null> {
    const { data: scan, error } = await this.client
      .from('repo_scans')
      .select('id, results')
      .eq('pull_request_id', prId)
      .order('scan_date', { ascending: false })
      .limit(1)
      .single();

    if (error || !scan || !scan.results) {
      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching PR review:', error);
      }
      return null;
    }

    const results = scan.results as ScanResults;
    return {
      id: scan.id,
      summary: results.summary || 'No summary provided.',
      issues: (results.issues as CodeIssue[]) || [],
    };
  }

  async getOpenPullRequests(): Promise<PullRequest[]> {
    const { data: userSession } = await this.client.auth.getUser();
    if (!userSession.user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await this.client
      .from('pull_requests')
      .select('*')
      .eq('user_id', userSession.user.id)
      .eq('state', 'open')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching open pull requests:', error);
      throw error;
    }

    return data || [];
  }

  async getRecentMerges(limit = 50): Promise<PullRequest[]> {
    const { data: userSession } = await this.client.auth.getUser();
    if (!userSession.user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await this.client
      .from('pull_requests')
      .select('*')
      .eq('user_id', userSession.user.id)
      .eq('state', 'merged')
      .order('closed_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching recent merges:', error);
      throw error;
    }

    return data || [];
  }

  async getWeeklyMetrics(repositoryId?: string): Promise<RepositoryMetrics> {
    const { data: userSession } = await this.client.auth.getUser();
    if (!userSession.user) {
      throw new Error('User not authenticated');
    }

    const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

    // 1. Fetch PRs merged this week
    let prsQuery = this.client
      .from('pull_requests')
      .select('merge_time_hours, lines_added, lines_deleted, lines_changed')
      .eq('user_id', userSession.user.id)
      .gte('merged_at', oneWeekAgo);

    if (repositoryId) {
      prsQuery = prsQuery.eq('repository_id', repositoryId);
    }

    const { data: prs, error: prsError } = await prsQuery;
    if (prsError) {
      console.error('Error fetching weekly PR metrics:', prsError);
      throw prsError;
    }

    // 2. Fetch Repo Scans this week
    let scansQuery = this.client
      .from('repo_scans')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userSession.user.id)
      .gte('scan_date', oneWeekAgo);

    if (repositoryId) {
      const repo = await this.getRepositoryById(repositoryId);
      if (repo) {
        scansQuery = scansQuery.eq('repo_url', repo.url);
      }
    }

    const { count: totalScans, error: scansError } = await scansQuery;
    if (scansError) {
      console.error('Error fetching weekly scan metrics:', scansError);
      throw scansError;
    }

    // Calculate aggregates
    const prsCount = prs?.length || 0;
    const avgMergeTime = prsCount > 0 ? prs.reduce((sum, p) => sum + (p.merge_time_hours || 0), 0) / prsCount : 0;
    const avgLinesAdded = prsCount > 0 ? prs.reduce((sum, p) => sum + (p.lines_added || 0), 0) / prsCount : 0;
    const avgLinesDeleted = prsCount > 0 ? prs.reduce((sum, p) => sum + (p.lines_deleted || 0), 0) / prsCount : 0;
    const avgLinesChanged = prsCount > 0 ? prs.reduce((sum, p) => sum + (p.lines_changed || 0), 0) / prsCount : 0;

    return {
      prsThisWeek: prsCount,
      totalScans: totalScans || 0,
      avgMergeTimeHours: Math.round(avgMergeTime * 10) / 10,
      avgPRLinesAdded: Math.round(avgLinesAdded),
      avgPRLinesDeleted: Math.round(avgLinesDeleted),
      avgPRLinesChanged: Math.round(avgLinesChanged),
      // Default values for unused fields
      totalPRsMerged: 0,
      openPRs: 0,
      prsThisMonth: 0,
      totalIssuesFound: 0,
      avgIssuesPerPR: 0,
      repoName: '',
      repoUrl: '',
      repositoryId: '',
      scanStatus: 'active',
      totalPRs: 0
    };
  }
}

// Add a helper method to String to generate a hash code for IDs
declare global {
  interface String {
    hashCode(): number;
  }
}

String.prototype.hashCode = function() {
  let hash = 0;
  for (let i = 0; i < this.length; i++) {
    const char = this.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

// Create and export a singleton instance
export const supabaseService = new SupabaseService();
